import { compileMotion, type CompiledTrack } from '../animation/procedural/compiler';
import { TEMPLATES } from '../animation/procedural/spec';
import { baseValue, poseOf, trackValueAt } from '../animation/timeline';
import { MAX_ANIMATION_SECONDS, type AnimationTrack, type AvMotionCue, type AvSession, type Character, type PoseKey, type Scene } from '../domain/schemas';
import type { ObjectReaction } from './reactions';
import { TICKS_PER_BAR, TICKS_PER_BEAT, ticksToSeconds } from './time';

/**
 * Motion cues (Phase 4A): Phase 3C procedural motions placed on the musical timeline.
 *
 * A cue is compiled ONCE by the Phase 3C motion compiler (`compileMotion`) — when the cue,
 * its object, the scene or the tempo changes — never during playback. Playback only samples
 * the compiled keyframes. During each occurrence the motion is applied as a non-destructive
 * overlay relative to the object's base pose (position/rotation/opacity/pose as offsets,
 * scale as a factor), composed like reactions; outside its occurrences a cue has no effect.
 * "Bake" (`applyMotionCues`) turns cues into permanent keyframes of the animation instead.
 */
export const CUE_FPS = 60;

export interface CompiledCue {
  objectId: string;
  durationSec: number;
  tracks: AnimationTrack[];
  error: string | null;
}

const cache = new WeakMap<AvMotionCue, { scene: Scene; characters: readonly Character[]; bpm: number; result: CompiledCue }>();
let compileCount = 0;
/** Number of cue compilations so far (diagnostics: playback must not increase it). */
export const cueCompilations = () => compileCount;

export function compileCue(cue: AvMotionCue, scene: Scene, characters: readonly Character[], bpm: number): CompiledCue {
  const hit = cache.get(cue);
  if (hit && hit.scene === scene && hit.characters === characters && hit.bpm === bpm) return hit.result;
  compileCount++;
  let result: CompiledCue;
  const o = scene.objects.find((x) => x.id === cue.objectId);
  if (!o) result = { objectId: cue.objectId, durationSec: 0, tracks: [], error: 'The cue’s object is not in this scene.' };
  else {
    try {
      const compiled = compileMotion(
        {
          version: 1,
          slots: [{ name: 'target', kind: o.type === 'character' ? 'character' : 'object', label: o.name }],
          steps: [
            {
              id: 'cue',
              template: cue.template,
              targets: ['target'],
              timing: { mode: 'at', at: 0 },
              duration: cue.durationBeats !== null ? ticksToSeconds(cue.durationBeats * TICKS_PER_BEAT, bpm) : null,
              params: cue.params,
            },
          ],
        },
        { scene, characters, binding: { target: o.id }, timing: { fps: CUE_FPS, durationFrames: MAX_ANIMATION_SECONDS * CUE_FPS }, existing: null, allowOverflow: true },
      );
      result = {
        objectId: o.id,
        durationSec: compiled.requiredFrames / CUE_FPS,
        tracks: compiled.tracks.map((t: CompiledTrack, i) => ({ id: `cue_${i}`, objectId: t.objectId, channel: t.channel, keyframes: t.keyframes })),
        error: null,
      };
    } catch (error) {
      result = { objectId: o.id, durationSec: 0, tracks: [], error: error instanceof Error ? error.message : 'The motion could not be compiled.' };
    }
  }
  cache.set(cue, { scene, characters, bpm, result });
  return result;
}

/** Ticks at which a cue starts (each repetition). */
export function cueOccurrences(cue: AvMotionCue): number[] {
  if (!cue.everyTicks) return [cue.tick];
  return Array.from({ length: cue.count }, (_, i) => cue.tick + i * cue.everyTicks!);
}

/** Human description: "Jump at 3.1 · every 4 bars × 8". */
export function describeCue(cue: AvMotionCue): string {
  const bar = Math.floor(cue.tick / TICKS_PER_BAR) + 1;
  const beat = Math.floor((cue.tick % TICKS_PER_BAR) / TICKS_PER_BEAT) + 1;
  const every = cue.everyTicks ? ` · every ${cue.everyTicks % TICKS_PER_BAR === 0 ? `${cue.everyTicks / TICKS_PER_BAR} bar${cue.everyTicks === TICKS_PER_BAR ? '' : 's'}` : `${cue.everyTicks / TICKS_PER_BEAT} beats`} × ${cue.count}` : '';
  return `${TEMPLATES[cue.template].label} at bar ${bar} beat ${beat}${every}`;
}

/**
 * Adds every cue active at `seconds` into `objects` (creating reactions as needed). Only the
 * most recent occurrence of a cue applies (a new occurrence restarts the motion).
 */
export function addCueOverlays(objects: Map<string, ObjectReaction>, session: Pick<AvSession, 'motionCues'>, scene: Scene, characters: readonly Character[], bpm: number, seconds: number, make: () => ObjectReaction): number {
  let active = 0;
  for (const cue of session.motionCues) {
    const compiled = compileCue(cue, scene, characters, bpm);
    if (compiled.error || compiled.durationSec <= 0) continue;
    const o = scene.objects.find((x) => x.id === compiled.objectId);
    if (!o) continue;
    // Latest occurrence at or before `seconds`.
    const startTick = ticksToSeconds(cue.tick, bpm);
    if (seconds < startTick) continue;
    let k = 0;
    if (cue.everyTicks) k = Math.min(cue.count - 1, Math.floor((seconds - startTick) / ticksToSeconds(cue.everyTicks, bpm) + 1e-9));
    const start = ticksToSeconds(cue.tick + k * (cue.everyTicks ?? 0), bpm);
    const t = seconds - start;
    if (t > compiled.durationSec) continue;
    active++;
    let r = objects.get(o.id);
    if (!r) objects.set(o.id, (r = make()));
    const frame = t * CUE_FPS;
    const pose = poseOf(o);
    for (const track of compiled.tracks) {
      const v = trackValueAt(track, frame);
      if (v === undefined) continue;
      switch (track.channel) {
        case 'x':
          r.dx += v - o.x;
          break;
        case 'y':
          r.dy += v - o.y;
          break;
        case 'rotation':
          r.rotation += v - o.rotation;
          break;
        case 'scaleX':
          r.sx *= v;
          break;
        case 'scaleY':
          r.sy *= v;
          break;
        case 'opacity':
          r.opacity += v - baseValue(o, 'opacity');
          break;
        case 'visible':
          if (v >= 0.5) r.show = true;
          else r.hide = true;
          break;
        default: {
          const k2 = track.channel.slice(5) as PoseKey;
          r.pose[k2] = (r.pose[k2] ?? 0) + v - pose[k2];
        }
      }
    }
  }
  return active;
}
